// Support / donations page. Linked from Help + Landing footer.
// Both Venmo and Stripe accept any amount; the donor picks at checkout.
//
// Donations are NOT tied to any in-app benefit, on purpose. That keeps us
// clear of Apple's IAP rules (guideline 3.2.1 allows donations to third
// parties outside IAP as long as no digital content/services are unlocked).

import { Link } from "react-router-dom";
import { Coffee, Mail } from "lucide-react";
import { Wordmark } from "../components/Brand";
import { Card, CardBody } from "../components/ui/Card";
import { STRIPE_CUSTOM_URL, STRIPE_LIVE, VENMO } from "../config/donations";

export function Support() {
  return (
    <div className="min-h-full bg-bg">
      <header className="border-b border-border bg-surface/80 backdrop-blur sticky top-0 z-10">
        <div className="mx-auto max-w-3xl px-5 h-14 flex items-center justify-between">
          <Link to="/" className="block"><Wordmark size="sm" /></Link>
          <Link to="/" className="text-sm text-text-muted hover:text-text">Back to home</Link>
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-5 py-12 sm:py-16">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-soft text-primary text-xs font-semibold border border-primary/15">
          <Coffee className="h-3.5 w-3.5" /> Free forever, ad-free
        </div>
        <h1 className="mt-5 text-3xl sm:text-4xl font-semibold tracking-tight">
          Using the app for your family? Buy John a coffee.
        </h1>
        <p className="mt-5 text-text-muted leading-relaxed">
          Carol Care is free for every family, and always will be — no paywalls, no ads, no upsells.
          The cloud bills are real though, and John covers them himself. If the app has been useful
          to your family and you feel like paying it forward, anything helps keep it running for the
          next family.
        </p>
        <p className="mt-3 text-sm text-text-faint">
          Not a registered nonprofit, so donations aren't tax-deductible. You pick the amount on
          {STRIPE_LIVE ? " Venmo or Stripe." : " Venmo."}
        </p>

        {/* Two equal-weight options */}
        <section className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <a
            href={VENMO.profileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="block rounded-xl border border-border bg-surface hover:border-primary/40 hover:shadow-[var(--shadow-lift)] transition-all p-5 group"
          >
            <div className="text-xs font-semibold uppercase tracking-wider text-text-faint">Venmo</div>
            <div className="mt-1 text-lg font-semibold tracking-tight text-text">@{VENMO.username}</div>
            <div className="mt-3 text-sm text-primary font-medium group-hover:underline">Open Venmo →</div>
          </a>

          {STRIPE_LIVE ? (
            <a
              href={STRIPE_CUSTOM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="block rounded-xl border border-border bg-surface hover:border-primary/40 hover:shadow-[var(--shadow-lift)] transition-all p-5 group"
            >
              <div className="text-xs font-semibold uppercase tracking-wider text-text-faint">Card via Stripe</div>
              <div className="mt-1 text-lg font-semibold tracking-tight text-text">A cup of coffee</div>
              <div className="mt-3 text-sm text-primary font-medium group-hover:underline">Open Stripe →</div>
            </a>
          ) : null}
        </section>

        <Card className="mt-10">
          <CardBody>
            <h2 className="font-semibold text-base flex items-center gap-2">
              <Mail className="h-4 w-4 text-primary" /> Prefer another way?
            </h2>
            <p className="mt-2 text-sm text-text-muted">
              Email <a href="mailto:support@carolcare.io" className="text-primary hover:underline">support@carolcare.io</a> if neither of these works and we'll figure it out.
            </p>
          </CardBody>
        </Card>

        <p className="mt-10 text-xs text-text-faint">
          What buying a coffee does <em>not</em> unlock: any in-app benefit, premium feature, badge, or extra anything. The app is free for everyone — it just helps keep it free for everyone.
        </p>

        <footer className="mt-12 pt-8 border-t border-border text-sm text-text-muted flex items-center justify-between">
          <Wordmark size="sm" />
          <Link to="/" className="hover:text-text">Back to home</Link>
        </footer>
      </main>
    </div>
  );
}
